import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';

// Uwierzytelnianie aplikacji.
//
// ZMIANA ISTOTNA MERYTORYCZNIE: usunięty został fallback signInAnonymously().
// W poprzedniej wersji App.js przy braku zalogowanego użytkownika logował go
// anonimowo — przez co ekran logowania nigdy się nie pokazywał (o ile metoda
// anonimowa była włączona w konsoli Firebase), a każdy odwiedzający dostawał
// dostęp do aplikacji i własną przestrzeń danych pod osobnym uid.
//
// UWAGA PRZY WDROŻENIU: dane są zapisane pod ścieżką zawierającą uid
// (artifacts/<app>/users/<uid>/...). Jeśli aplikacja pracowała dotąd na koncie
// anonimowym, dane leżą pod anonimowym uid i po zalogowaniu e-mailem zobaczysz
// pustą bazę. Przed wdrożeniem sprawdź w konsoli Firebase (Firestore ->
// artifacts -> default-app-id -> users), które uid zawiera Twoje kontenery,
// i porównaj je z uid konta e-mail w zakładce Authentication.

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsAuthReady(true);
    });

    return unsubscribe;
  }, []);

  // Zwraca komunikat błędu zamiast rzucać — formularz logowania i tak musi
  // wyświetlić go użytkownikowi, a surowe komunikaty Firebase są nieczytelne.
  const login = useCallback(async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { ok: true };
    } catch (error) {
      return { ok: false, error: describeAuthError(error) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      return { ok: true };
    } catch (error) {
      console.error('Błąd wylogowania:', error);
      return { ok: false, error: 'Nie udało się wylogować. Spróbuj ponownie.' };
    }
  }, []);

  const value = useMemo(
    () => ({ user, userId: user?.uid || null, isAuthReady, login, logout }),
    [user, isAuthReady, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Tłumaczy kody błędów Firebase Auth na komunikaty zrozumiałe dla operatora. */
function describeAuthError(error) {
  switch (error?.code) {
    case 'auth/invalid-email':
      return 'Nieprawidłowy format adresu e-mail.';
    case 'auth/user-disabled':
      return 'To konto zostało zablokowane.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Nieprawidłowy e-mail lub hasło.';
    case 'auth/too-many-requests':
      return 'Zbyt wiele prób logowania. Odczekaj chwilę i spróbuj ponownie.';
    case 'auth/network-request-failed':
      return 'Brak połączenia z siecią. Sprawdź internet i spróbuj ponownie.';
    default:
      console.error('Błąd logowania:', error);
      return 'Logowanie nie powiodło się. Spróbuj ponownie.';
  }
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth musi być użyty wewnątrz <AuthProvider>');
  return context;
}
